import React, { useMemo } from 'react';
import AddIcon from '@mui/icons-material/Add';
import LockIcon from '@mui/icons-material/Lock';
import { Box, Typography, Paper, Button, CircularProgress, Tooltip } from '@mui/material';
import { colors } from '@src/styles';
import { staggerChildrenSX } from '@src/styles/animations';
import { getDefinitionStatus } from './utils/canonicalUtils';
import { monoFontFamily, monoLabelSX, titleFontFamily, tonePillSX } from './canonicalStyles';
import { ResourceDefinitionDTO } from '@src/services/brain/modules/v1/resources/dto/brain-resources.get.response.dto';

export interface DefinitionsListSectionProps {
  organizationDefinitions: ResourceDefinitionDTO[] | null;
  organizationDefinitionsError?: { message: string; code?: number } | null;
  isLoadingOrganizationDefinitions: boolean;
  hasMoreDefinitions: boolean;
  selectedDefinitionId: string | null;
  currentPublishedDefinitionId?: string;
  onSelectDefinition: (definitionId: string) => void;
  onLoadMore: () => void;
  onOpenNewDefinitionModal: () => void;
}

type TVersionStatus = { label: string; color: string };

const getVersionStatus = (definition: ResourceDefinitionDTO, currentPublishedDefinitionId?: string): TVersionStatus => {
  if (!definition.publishedAt) return { label: 'Rascunho', color: colors.brand.textMuted };
  if (definition.id === currentPublishedDefinitionId) return { label: 'Publicada', color: colors.status.concluded };
  return { label: 'Publicada antes', color: colors.status.pending };
};

const formatDate = (value: string): string =>
  new Date(value).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });

const DefinitionsListSection: React.FC<DefinitionsListSectionProps> = ({
  organizationDefinitions,
  organizationDefinitionsError,
  isLoadingOrganizationDefinitions,
  hasMoreDefinitions,
  selectedDefinitionId,
  currentPublishedDefinitionId,
  onSelectDefinition,
  onLoadMore,
  onOpenNewDefinitionModal,
}) => {
  const sortedDefinitions = useMemo(
    () =>
      [...(organizationDefinitions ?? [])].sort(
        (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
      ),
    [organizationDefinitions],
  );

  return (
    <Paper
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5,
        width: '20%',
        minWidth: 220,
        padding: '14px',
        border: `1px solid ${colors.brand.line}`,
        borderRadius: '12px',
        backgroundColor: colors.brand.surface,
        backgroundImage: 'none',
        boxShadow: 'none',
        overflowY: 'auto',
        '&::-webkit-scrollbar': { display: 'none' },
        scrollbarWidth: 'none',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography sx={{ ...monoLabelSX, flexGrow: 1 }}>
          Versões{organizationDefinitions ? ` · ${organizationDefinitions.length}` : ''}
        </Typography>
        <Button
          onClick={onOpenNewDefinitionModal}
          startIcon={<AddIcon sx={{ fontSize: 16 }} />}
          sx={{
            height: 30,
            paddingX: 1.25,
            minWidth: 'auto',
            borderRadius: '8px',
            border: '1px solid rgba(255, 160, 21, 0.5)',
            backgroundColor: 'rgba(255, 160, 21, 0.1)',
            color: colors.primary.yellow,
            fontFamily: titleFontFamily,
            fontSize: '12.5px',
            fontWeight: 600,
            textTransform: 'none',
            '& .MuiButton-startIcon': { marginRight: '4px' },
            '&:hover': { backgroundColor: 'rgba(255, 160, 21, 0.18)', borderColor: colors.primary.yellow },
          }}
        >
          Nova
        </Button>
      </Box>

      {organizationDefinitionsError ? (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 1.25,
            padding: '24px 8px',
            textAlign: 'center',
          }}
        >
          <LockIcon sx={{ fontSize: 32, color: colors.status.rejected }} />
          <Typography sx={{ fontSize: '13px', fontWeight: 500, color: colors.brand.textStrong }}>
            {organizationDefinitionsError.message}
          </Typography>
          {organizationDefinitionsError.code && (
            <Box sx={tonePillSX(colors.status.rejected)}>Erro {organizationDefinitionsError.code}</Box>
          )}
        </Box>
      ) : isLoadingOrganizationDefinitions && !organizationDefinitions ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', padding: 2 }}>
          <CircularProgress size={22} sx={{ color: colors.primary.yellow }} />
        </Box>
      ) : sortedDefinitions.length > 0 ? (
        <>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, ...staggerChildrenSX('button', 10, 25) }}>
            {sortedDefinitions.map((definition) => {
              const isSelected = selectedDefinitionId === definition.id;
              const status = getVersionStatus(definition, currentPublishedDefinitionId);
              return (
                <Tooltip
                  key={definition.id}
                  title={
                    getDefinitionStatus(definition.id, definition.publishedAt, currentPublishedDefinitionId).message ??
                    ''
                  }
                  placement="right"
                  arrow
                >
                  <Box
                    component="button"
                    type="button"
                    onClick={() => onSelectDefinition(definition.id)}
                    aria-current={isSelected ? 'true' : undefined}
                    sx={{
                      position: 'relative',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'flex-start',
                      gap: 0.5,
                      width: '100%',
                      padding: '9px 10px 9px 12px',
                      textAlign: 'left',
                      cursor: 'pointer',
                      borderRadius: '10px',
                      border: `1px solid ${isSelected ? 'rgba(255, 160, 21, 0.45)' : colors.brand.line}`,
                      backgroundColor: isSelected ? 'rgba(255, 160, 21, 0.08)' : colors.background.primary,
                      transition: 'border-color 0.15s ease, background-color 0.15s ease',
                      '&:hover': { borderColor: colors.primary.yellow },
                      '&:focus-visible': { outline: `2px solid ${colors.primary.yellow}`, outlineOffset: '2px' },
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        left: 0,
                        top: 9,
                        bottom: 9,
                        width: 3,
                        borderRadius: '0 3px 3px 0',
                        backgroundColor: isSelected ? colors.primary.yellow : 'transparent',
                      },
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                      <Typography
                        sx={{
                          flexGrow: 1,
                          fontFamily: monoFontFamily,
                          fontSize: '13px',
                          fontWeight: 500,
                          color: isSelected ? colors.primary.white : colors.brand.textStrong,
                        }}
                      >
                        v{definition.version}
                      </Typography>
                      <Box sx={tonePillSX(status.color)}>{status.label}</Box>
                    </Box>
                    <Typography sx={{ fontFamily: monoFontFamily, fontSize: '10.5px', color: colors.brand.textMuted }}>
                      criada em {formatDate(definition.createdAt)}
                    </Typography>
                  </Box>
                </Tooltip>
              );
            })}
          </Box>

          {hasMoreDefinitions && (
            <Button
              onClick={onLoadMore}
              disabled={isLoadingOrganizationDefinitions}
              sx={{
                height: 32,
                borderRadius: '8px',
                border: `1px solid ${colors.brand.line}`,
                color: colors.brand.textStrong,
                fontFamily: titleFontFamily,
                fontSize: '12.5px',
                textTransform: 'none',
                '&:hover': { borderColor: colors.primary.yellow, backgroundColor: 'transparent' },
              }}
            >
              {isLoadingOrganizationDefinitions ? (
                <CircularProgress size={16} sx={{ color: colors.primary.yellow }} />
              ) : (
                'Carregar mais'
              )}
            </Button>
          )}
        </>
      ) : (
        <Typography sx={{ padding: '16px 4px', fontSize: '13px', color: colors.brand.textMuted, textAlign: 'center' }}>
          Nenhuma versão ainda. Use "Nova" para criar a primeira.
        </Typography>
      )}
    </Paper>
  );
};

export default DefinitionsListSection;
